import { CircleDollarSign } from '@tamagui/lucide-icons';
import { Loader } from '@unistocks/components/loading/spinner/loader';
import { NoData } from '@unistocks/components/no-data/no-data';
import { H2 } from '@unistocks/components/text/text';
import { savingsGoalSchemaFactory } from '@unistocks/features/savings-goals/components/forms/schema';
import {
  SavingsGoalCalculationDto,
  SavingsGoalCreateOrUpdateDto
} from '@unistocks/proxy/axios-client';
import {
  SavingsGoalsGetSavingsGoalPreviewSavingsGoalsQueryParameters as SavingsGoalsGetSavingsGoalPreviewQueryParameters,
  useSavingsGoalsGetSavingsGoalPreviewQuery
} from '@unistocks/proxy/axios-client/SavingsGoalsQuery';
import { t } from '@unistocks/shared/localization/index.ts';
import React from 'react';

const SavingsGoalChart = React.lazy(
  () => import('@unistocks/features/savings-goals/components/chart/savings-goal-chart')
);

export type SavingsGoalPreviewProps = SavingsGoalCreateOrUpdateDto;

export const SavingsGoalPreview: React.FC<SavingsGoalPreviewProps> = props => {
  const result = savingsGoalSchemaFactory().safeParse(props);
  const transformedValues = result.data as
    | SavingsGoalsGetSavingsGoalPreviewQueryParameters
    | undefined;
  const isValid = result.success;

  const { data, isLoading, isSuccess } = useSavingsGoalsGetSavingsGoalPreviewQuery(
    transformedValues!,
    {
      enabled: isValid
    }
  );

  if (isValid && isLoading) {
    return <Loader>{t('::SavingsGoal:Preview:Loading')}</Loader>;
  }

  return isSuccess ? (
    <>
      <H2 size={'$8'} color={'$headingFont'} textAlign="center">
        {t('::SavingsGoal:Preview:Heading')}
      </H2>
      <React.Suspense fallback={<Loader />}>
        <SavingsGoalChart
          savingsGoal={data.savingsGoal}
          goalCalculation={
            { goal: data.goal, progress: data.progress } as SavingsGoalCalculationDto
          }
          chartContainerProps={{ h: 300, $sm: { h: 400 }, $md: { h: 500 } }}
        />
      </React.Suspense>
    </>
  ) : (
    <NoData size={'$5'} Icon={CircleDollarSign} text={t('::SavingsGoal:Create:NoData')} />
  );
};

SavingsGoalPreview.displayName = 'SavingsGoalPreview';
